import { Alert, Box, DialogActions, DialogContent, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getDocumentsAvailablesForFactoring } from '../../../../../../../../lib/api';
import CustomButton from '../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../../../lib/designSystem/HeaderWithActions';
import { LightTooltip } from '../../../../../../../../lib/designSystem/WhiteTooltip';
import { formatMoney } from '../../../../../../../../lib/functions/utils';

interface EditorProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedRequest: any;
  searched: boolean;
  setSearched: (searched: boolean) => void;
  subProductSelected: any;
  businessIdentifier: string;
  subProduct: any;
  selectedDocuments: any;
  setSelectedDocuments: (selectedDocuments: any) => void;
  setOpenShoppingCart: (openShoppingCart: boolean) => void;
}

function getSelectedDocuments(documents: any[], allDocuments: any[]) {
  const selectedDocuments: any[] = [];
  const fatherDocumentsIds: any[] = [];
  for (const document of documents) {
    if (
      document.fatherDocumentId &&
      !fatherDocumentsIds.includes(document.fatherDocumentId)
    ) {
      fatherDocumentsIds.push(document.fatherDocumentId);
    } else if (!document.fatherDocumentId) {
      selectedDocuments.push({ ...document, isOld: true });
    }
  }
  for (const fatherDocumentId of fatherDocumentsIds) {
    const fatherDocument = allDocuments.find(
      (document: any) => document.id === fatherDocumentId,
    );
    const childDocuments = documents.filter(
      (document: any) => document.fatherDocumentId === fatherDocumentId,
    );
    selectedDocuments.push({
      ...fatherDocument,
      subDocuments: childDocuments.map((childDocument: any) => ({
        ...childDocument,
        isOld: true,
      })),
    });
  }
  return selectedDocuments.map((document: any) => ({
    ...document,
    subDocuments: document.subDocuments || [],
  }));
}

const Editor = (props: EditorProps) => {
  const {
    open,
    setOpen,
    selectedRequest,
    subProductSelected,
    businessIdentifier,
    subProduct,
    selectedDocuments,
    setSelectedDocuments,
    setOpenShoppingCart,
  } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [documents, setDocuments] = React.useState<any>([]);
  const [searchedBySubProduct, setSearchedBySubProduct] = React.useState(false);
  const [isEnrolled, setIsEnrolled] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<any>([]);
  const [configuration, setConfiguration] = useState<any>(null);

  // Filters
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [requestId, setRequestId] = useState('');
  const [enrolledType, setEnrolledType] = useState<any>(null);
  const [folio, setFolio] = useState('');
  const [cachedDocuments, setCachedDocuments] = useState<any>([]);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (folio) query += `folio=${folio}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (stakeholderName) query += `stakeholderName=${stakeholderName}&`;
    if (requestId) query += `requestId=${requestId}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}&`;
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (enrolledType) query += `enrolledType=${enrolledType}&`;
    if (selectedIds.length)
      query += `selectedDocuments=${selectedIds.join(',')}`;
    return query;
  };

  const filters: Filter[] = [
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de confirmación',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Nombre deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut contraparte',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
  ];
  if (isEnrolled) {
    filters.push({
      label: 'Tipo de inyección',
      type: 'select',
      options: [
        { value: 'sii', label: 'SII' },
        { value: 'manual', label: 'Creados manualmente' },
      ],
      value: enrolledType,
      setter: setEnrolledType,
    });
  }

  const handleSetSelected = (selectedIds: any) => {
    const newCachedDocuments = [...cachedDocuments];
    for (const id of selectedIds) {
      const document = documents.find((document: any) => document.id === id);
      if (document) {
        const newCachedIds = [
          ...newCachedDocuments.map((document: any) => document.id),
        ];
        if (!newCachedIds.includes(document.id)) {
          newCachedDocuments.push(document);
        }
      }
    }
    setCachedDocuments(newCachedDocuments);
    const uniqueIds = [...new Set(selectedIds)];
    setSelectedIds(uniqueIds);
  };
  const searchSuggestedDocuments = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const query = generateQuery({
      page,
      pageSize,
    });
    const response = await getDocumentsAvailablesForFactoring(
      dispatch,
      businessIdentifier,
      subProductSelected,
      query,
    );
    return response;
  };

  const handleSearchSuggestedDocuments = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      const response = await searchSuggestedDocuments({
        page,
        pageSize,
      });
      const allDocuments = [...cachedDocuments, ...response.data];
      const configuration = response.configuration;
      const documents = response.data;
      const pagination = response.pagination;
      const isEnrolled = response.isEnrolled;
      setConfiguration(configuration);
      setDocuments([...documents]);
      setCachedDocuments([...allDocuments]);
      setPagination(pagination);
      setIsEnrolled(isEnrolled);
      setSearchedBySubProduct(true);
      return response;
    } catch (error) {
      console.log(error);
    }
  };

  const cantSelectDocuments = (document: any) => {
    if (enrolledType === 'manual') {
      return false;
    }
    if (configuration.dontFilterOwnInvoices) {
      if (document.cessionaryIdentifier === configuration.companyIdentifier) {
        return false;
      }
    }
    if (configuration.showOnlyAvailables) {
      if (document.status === 'Transable') {
        return false;
      }
    }
    if (
      !configuration.dontFilterOwnInvoices &&
      !configuration.showOnlyAvailables
    ) {
      return false;
    }
    return true;
  };

  const handleSetPrefill = (documents: any) => {
    const selectedDocs = getSelectedDocuments(documents, cachedDocuments);
    let preSelectedIds = [];
    if (selectedDocuments.length) {
      preSelectedIds = selectedDocuments.map((document: any) => document.id);
      setCachedDocuments(selectedDocuments);
    } else {
      preSelectedIds = selectedDocs.map((document: any) => document.id);
      setCachedDocuments(selectedDocs);
    }
    const uniquePreSelectedIds = [...new Set(preSelectedIds)];
    setSelectedIds(uniquePreSelectedIds);
  };

  const triggerSearch = async () => {
    setLoading(true);
    await handleSearchSuggestedDocuments({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
    setLoading(false);
  };

  const handleSelectDocuments = async () => {
    let documentsSelected: any[] = cachedDocuments.filter((document: any) =>
      selectedIds.includes(document.id),
    );
    // Remove duplicates from documentsSelected based on the document id
    const uniqueDocumentsSelected = documentsSelected.filter(
      (document, index, self) =>
        index === self.findIndex((doc) => doc.id === document.id),
    );
    documentsSelected = uniqueDocumentsSelected;
    if (!documentsSelected) {
      return;
    }
    // Add paymentDate from selectedDocuments to documentsSelected in case it exists
    documentsSelected.forEach((document: any) => {
      const selectedDocument = selectedRequest.documents.find(
        (doc: any) => doc.id === document.id,
      );
      document.amountAvailable = document.amountAvailable
        ? document.amountAvailable
        : document.amount;
      if (selectedDocument) {
        if (selectedDocument.paymentDate) {
          document.dueDate = selectedDocument.paymentDate;
        }
        if (selectedDocument.subDocuments) {
          document.subDocuments = selectedDocument.subDocuments;
          document.subDocuments.forEach((subDocument: any) => {
            if (subDocument.paymentDate) {
              subDocument.dueDate = subDocument.paymentDate;
            }
          });
        }
      }
    });
    setSelectedDocuments(documentsSelected);
    setOpenShoppingCart(true);
    setOpen(false);
  };

  const setSelectedDocumentsInit = () => {
    handleSetPrefill(selectedRequest.documents);
  };

  const setup = async () => {
    await handleSearchSuggestedDocuments({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
    setSelectedDocumentsInit();
  };

  useEffect(() => {
    setup();
  }, []);

  const renderCreditNoteAlert = (creditNotesValue: number) => {
    const isWarning = creditNotesValue > 0;
    const alertProps = isWarning
      ? {
          severity: 'warning' as const,
          tooltip: (
            <Box p={1}>
              <Typography fontSize={16}>
                {`Contiene Notas de crédito por: ${formatMoney(creditNotesValue)}`}
              </Typography>
            </Box>
          ),
        }
      : {
          severity: 'success' as const,
          tooltip: (
            <Box p={1}>
              <Typography fontSize={16}>No hay notas de crédito.</Typography>
            </Box>
          ),
        };
    return (
      <LightTooltip title={alertProps.tooltip} placement="top">
        <span>
          <Alert
            severity={alertProps.severity}
            sx={{
              maxWidth: '20px',
              minWidth: '20px',
              height: '20px',
              p: 0,
              m: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiAlert-icon': {
                fontSize: '1rem',
                m: 0,
              },
            }}
          />
        </span>
      </LightTooltip>
    );
  };

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <DialogTitle>
        <HeaderWithActions title="Editar Documentos" type="dialogHeader" />
      </DialogTitle>

      <DialogContent>
        {!searchedBySubProduct ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <div style={{ padding: '0px 0px 20px 0px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <Typography variant="h6" gutterBottom>
                  Agregar nuevos documentos{' '}
                  {isEnrolled ? '(Conectado SII)' : '(No conectado SII)'}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Estas son los documentos disponibles para factorizar en el
                  subproducto {subProduct}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedIds.length} documentos seleccionados
                </Typography>
              </div>
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerSearch}
                dispatch={dispatch}
                disableSearch={loading}
              />
            </div>
            <div>
              <TableWithPagination
                dataHeaders={[
                  { key: 'folio', label: 'Folio' },
                  ...(selectedDocuments[0]?.code
                    ? [{ key: 'code', label: 'Código Doc.' }]
                    : []),
                  ...(documents.some(
                    (doc: any) => doc.creditNoteAmount !== undefined,
                  )
                    ? [
                        {
                          key: 'creditNoteAmount',
                          label: '',
                          type: 'custom',
                          customComponent: (value: any) => {
                            return renderCreditNoteAlert(Number(value) || 0);
                          },
                        },
                      ]
                    : []),
                  { key: 'amount', label: 'Monto', type: 'money' },
                  {
                    key: 'amountAvailable',
                    label: 'Monto disponible',
                    type: 'money',
                  },
                  {
                    key: 'stakeholderName',
                    label: 'Nombre receptor',
                  },
                  {
                    key: 'issuedDate',
                    label: 'Fecha de emisión',
                    type: 'eight-days-date',
                  },
                  {
                    key: 'confirmDate',
                    label: 'Fecha de confirmación',
                    type: 'date',
                  },
                  {
                    key: 'status',
                    label: 'Estado',
                    type: 'cession',
                  },
                ]}
                data={documents}
                selected={selectedIds}
                setSelected={handleSetSelected}
                searchData={searchSuggestedDocuments}
                setData={setDocuments}
                pagination={pagination}
                setPagination={setPagination}
                loading={loading}
                setLoading={setLoading}
                cantSelectRow={cantSelectDocuments}
              />
            </div>
          </div>
        )}
        <DialogActions>
          <CustomButton
            variant="contained"
            color="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {' '}
            Salir
          </CustomButton>
          {searchedBySubProduct && (
            <CustomButton
              variant="contained"
              color="primary"
              disabled={!selectedIds.length}
              onClick={handleSelectDocuments}
            >
              {' '}
              Seleccionar
            </CustomButton>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default Editor;
